import React, { useState, useEffect, useCallback } from 'react';
import { AppLayout } from '../../app/appLayout.js';
import { transactionsApi } from '../../../api/transactionsApi.js';
import { Modal } from '../../shared/index.js';
import './viewTransactions.css';

const formatAsYYYYMMDD = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
};

const ViewTransactions = () => {
  const [startDate, setStartDate] = useState(() => formatAsYYYYMMDD(daysAgo(30)));
  const [endDate, setEndDate] = useState(() => formatAsYYYYMMDD(new Date()));
  const [transactions, setTransactions] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [formData, setFormData] = useState({
    postedOn: formatAsYYYYMMDD(new Date()),
    type: '',
    description: '',
    vendor: '',
    amount: '',
    source: '',
    note: '',
    category: '',
    subCategory: ''
  });
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const totalItems = transactions.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const pagedTransactions = transactions.slice(startIndex, endIndex);


  const fetchItemDetails = useCallback(async (s, e) => {
    try {
      const res = await transactionsApi.getItemDetails(s, e);
      if(res?.msg == "Forbidden") {
        window.location.href = '/login';
        return;
      }
      if(res?.msg === "Success" && res?.data?.rows) {
        setTransactions(res.data.rows);
        setCurrentPage(1);
      }
    } catch (err) {
      console.error('Failed to fetch item details:', err);
    }
  }, []);

  useEffect(() => {
    fetchItemDetails(startDate, endDate);
  }, [fetchItemDetails, startDate, endDate]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await transactionsApi.getCategories();
        if(res?.msg === "Success" && res?.data?.rows) {
          setCategories(res.data.rows);
        }
      } catch (err) {
        console.error('Failed to fetch categories:', err);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchSubCategories = async () => {
      if (!formData.category || formData.category === '') {
        setSubCategories([]);
        return;
      }
      try {
        const res = await transactionsApi.getSubCategories(formData.category);
        if(res?.msg === "Success" && res?.data?.rows) {
          setSubCategories(res.data.rows);
        }
      } catch (err) {
        console.error('Failed to fetch subcategories:', err);
      }
    };
    fetchSubCategories();
  }, [formData.category]);

  const onSubmit = (e) => {
    e.preventDefault();
    fetchItemDetails(startDate, endDate);
  };

  const handleUpload = () => {
    console.log('Upload button clicked');
  };

  const resetForm = () => {
    setFormData({
      postedOn: formatAsYYYYMMDD(new Date()),
      type: '',
      description: '',
      vendor: '',
      amount: '',
      source: '',
      note: '',
      category: '',
      subCategory: ''
    });
    setEditingTransaction(null);
  };

  const handleCreateSingle = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleEdit = (transaction) => {
    const postedOnDate = transaction.postedOn?.date 
      ? transaction.postedOn.date.split(' ')[0] 
      : formatAsYYYYMMDD(new Date());
    
    setFormData({
      postedOn: postedOnDate,
      type: transaction.type || '',
      description: transaction.description || '',
      vendor: transaction.vendor || '',
      amount: transaction.amount?.toString() || '',
      source: transaction.source || '',
      note: transaction.note || '',
      category: transaction.category || '',
      subCategory: transaction.subCategory || ''
    });
    setEditingTransaction(transaction.id);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      // If category changes, reset subCategory
      if (name === 'category') {
        newData.subCategory = '';
      }
      return newData;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data with blank strings for NONE selections
      const submitData = {
        ...formData,
        category: formData.category === '' ? '' : formData.category,
        subCategory: formData.subCategory === '' ? '' : formData.subCategory
      };
      
      if (editingTransaction) {
        await transactionsApi.updateTransaction(editingTransaction, submitData);
      } else {
        await transactionsApi.createSingle(submitData);
      }
      // Refresh the transactions list
      await fetchItemDetails(startDate, endDate);
    } catch (err) {
      console.error('Failed to save transaction', err);
    }
    setIsModalOpen(false);
    resetForm();
  };

  const handleFormCancel = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = dateString.split(' ')[0];
    const [year, month, day] = date.split('-');
    return `${month}/${day}/${year}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getTypeIcon = (type) => {
    return type === 'WITHDRAWL' ? '↓' : '↑';
  };

  const getSourceIcon = (source) => {
    if (!source) return null;
    const upperSource = source.toUpperCase();
    if (upperSource === 'SCHWAB') {
      return <span className="source-icon source-schwab">S</span>;
    } else if (upperSource === 'CHASE') {
      return <span className="source-icon source-chase">C</span>;
    }
    return null;
  };

  return (
    <AppLayout>
      <div className="transactions-container">
        <div className="transactions-header">
          <h1>Transactions</h1>
        </div>

        <div className="action-buttons">
          <button className="btn btn-primary" onClick={handleCreateSingle}>
            Create single
          </button>
          <button className="btn btn-secondary" onClick={handleUpload}>
            Upload
          </button>
        </div>

        <form className="date-filter-form" onSubmit={onSubmit}>
          <div className="form-group">
            <label htmlFor="startDate">Start date</label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="endDate">End date</label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <button type="submit" className="btn btn-primary">Apply</button>
        </form>
        <div className="pagination-bar">
          <div className="pagination-left">
            <span className="pagination-summary">
              Showing {totalItems === 0 ? 0 : startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems}
            </span>
          </div>

          <div className="pagination-right">
            <label className="page-size-label">
              Page size
              <select
                className="page-size-select"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              type="button"
            >
              ⏮ First
            </button>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              type="button"
            >
              ◀ Prev
            </button>

            <span className="pagination-page">
              Page {currentPage} / {totalPages}
            </span>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              type="button"
            >
              Next ▶
            </button>

            <button
              className="btn btn-secondary pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              type="button"
            >
              Last ⏭
            </button>
          </div>
        </div>

        <div className="transactions-table-container">
          {transactions.length === 0 ? (
            <div className="empty-state">
              <p>No transactions found for the selected date range.</p>
            </div>
          ) : (
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Posted On</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Vendor</th>
                  <th>Amount</th>
                  <th>Source</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Note</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
              {pagedTransactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.id}</td>
                    <td>{formatDate(transaction.postedOn?.date)}</td>
                    <td>
                      <div className="type-cell">
                        <span className={`type-badge type-${transaction.type?.toLowerCase()}`}>
                          <span className="type-icon">{getTypeIcon(transaction.type)}</span>
                          {transaction.type}
                        </span>
                      </div>
                    </td>
                    <td>{transaction.description}</td>
                    <td>{transaction.vendor}</td>
                    <td className={`amount-cell ${transaction.type === 'DEPOSIT' ? 'amount-positive' : 'amount-negative'}`}>
                      {transaction.type === 'DEPOSIT' ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount))}
                    </td>
                    <td>
                      <div className="source-cell">
                        {getSourceIcon(transaction.source)}
                        <span>{transaction.source}</span>
                      </div>
                    </td>
                    <td>{transaction.category || '-'}</td>
                    <td>{transaction.subCategory || '-'}</td>
                    <td>{transaction.note || '-'}</td>
                    <td>
                      <button className="edit-button" onClick={() => handleEdit(transaction)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {isModalOpen && (
        <Modal title={editingTransaction ? "Edit Transaction" : "Create Transaction"} onClose={handleModalClose}>
          <form className="modal-form" onSubmit={handleFormSubmit}>
            <div className="modal-form-group">
              <label htmlFor="postedOn">
                Posted on<span className="required-asterisk">*</span>
              </label>
              <input
                id="postedOn"
                name="postedOn"
                type="date"
                value={formData.postedOn}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="modal-form-group">
              <label htmlFor="type">
                Type<span className="required-asterisk">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleFormChange}
                required
              >
                <option value="">Select type</option>
                <option value="WITHDRAWL">WITHDRAWL</option>
                <option value="DEPOSIT">DEPOSIT</option>
              </select>
            </div>

            <div className="modal-form-group">
              <label htmlFor="description">
                Description<span className="required-asterisk">*</span>
              </label>
              <input
                id="description"
                name="description"
                type="text"
                value={formData.description}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="modal-form-group">
              <label htmlFor="vendor">
                Vendor<span className="required-asterisk">*</span>
              </label>
              <input
                id="vendor"
                name="vendor"
                type="text"
                value={formData.vendor}
                onChange={handleFormChange}
                required
              />
            </div>

            <div className="modal-form-group">
              <label htmlFor="amount">
                Amount<span className="required-asterisk">*</span>
              </label>
              <input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={handleFormChange}
                required
                placeholder="0.00"
              />
            </div>

            <div className="modal-form-group">
              <label htmlFor="source">
                Source<span className="required-asterisk">*</span>
              </label>
              <select
                id="source"
                name="source"
                value={formData.source}
                onChange={handleFormChange}
                required
              >
                <option value="">Select source</option>
                <option value="SCHWAB">SCHWAB</option>
                <option value="CHASE">CHASE</option>
              </select>
            </div>

            <div className="modal-form-group">
              <label htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleFormChange}
              >
                <option value="">NONE</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-form-group">
              <label htmlFor="subCategory">Sub Category</label>
              <select
                id="subCategory"
                name="subCategory"
                value={formData.subCategory}
                onChange={handleFormChange}
                disabled={!formData.category || formData.category === ''}
              >
                <option value="">NONE</option>
                {subCategories.map((subCat) => (
                  <option key={subCat} value={subCat}>
                    {subCat}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-form-group">
              <label htmlFor="note">Note</label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleFormChange}
                rows={4}
              />
            </div>

            <div className="modal-form-actions">
              <button type="button" className="btn btn-secondary" onClick={handleFormCancel}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {editingTransaction ? 'Update' : 'Submit'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </AppLayout>
  );
};

export { ViewTransactions };
